import { useEffect, useRef, useState } from 'react';
import { settings, uploadLogo, type TenantSettings } from '../lib/api.ts';
import { applyBranding } from '../lib/branding.ts';

/**
 * White-label configuration.
 *
 * Everything here is a database row, so a rebrand is a form submission
 * rather than a deploy. The colour drives the tonal ramp, the PWA
 * manifest, and the runtime-rendered app icons from one value.
 */
export const Settings = () => {
  const [current, setCurrent] = useState<TenantSettings | null>(null);
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [primary, setPrimary] = useState('#0F6E56');
  const [opens, setOpens] = useState('08:00');
  const [closes, setCloses] = useState('18:00');
  const [quiet, setQuiet] = useState(true);
  const [windowDays, setWindowDays] = useState('90');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  const [logoV, setLogoV] = useState(0);
  const [plate, setPlate] = useState(false);
  const logoInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    settings
      .get()
      .then((s) => {
        setCurrent(s);
        setName(s.identity?.product_name ?? '');
        setShortName(s.identity?.short_name ?? '');
        setPrimary(s.theme?.primary ?? '#0F6E56');
        setDock(s.display?.dock ?? 'off');
        setDockWidth(s.display?.dockWidth ?? 420);
        setOpens(s.hours?.opens_at?.slice(0, 5) ?? '08:00');
        setCloses(s.hours?.closes_at?.slice(0, 5) ?? '18:00');
        setQuiet(s.hours?.quiet_hours ?? true);
        setWindowDays(String(s.board?.window_days ?? 90));
        setLogoV(s.identity?.logo_version ?? 0);
        setPlate(s.identity?.logo_plate ?? false);
      })
      .catch(() => undefined);
  }, []);

  const pickLogo = async (file?: File) => {
    if (!file) return;
    setSaved('');
    try {
      await uploadLogo(file);
      // Re-read so the preview picks up the new version rather than
      // showing a cached copy of the old one.
      const s = await settings.get();
      setLogoV(s.identity?.logo_version ?? 0);
      setSaved('Logo updated.');
    } catch {
      setSaved('That file was refused. PNG or JPEG only.');
    }
  };

  const [dock, setDock] = useState('off');
  const [dockWidth, setDockWidth] = useState(420);

  const save = async () => {
    setBusy(true);
    setSaved('');
    try {
      const next = await settings.patch({
        display: {
          dock: dock as 'off' | 'left' | 'right',
          dockWidth,
        },
        identity: {
          product_name: name.trim(),
          short_name: shortName.trim(),
          logo_plate: plate,
        },
        theme: { primary },
        hours: { opens_at: opens, closes_at: closes, quiet_hours: quiet },
        board: { window_days: Number(windowDays) || 90 },
      });

      setCurrent(next);
      // Repaint immediately so the admin sees the result rather than
      // wondering whether it took.
      applyBranding({
        slug: '',
        name: next.identity?.product_name ?? '',
        ...(next.theme ? { theme: next.theme } : {}),
      });
      setSaved('Saved.');
    } catch {
      setSaved('Could not save. Check the values and try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!current) return <p className="empty">Loading...</p>;

  return (
    <div className="noticeWrap">
      {saved && <p className="allClear">{saved}</p>}

      <section className="composer">
        <p className="mono">Identity</p>
        <div className="noticeRow">
          <input
            value={name}
            placeholder="Product name"
            onChange={(e) => setName(e.target.value)}
          />
          <input
            value={shortName}
            placeholder="Short name"
            maxLength={20}
            onChange={(e) => setShortName(e.target.value)}
          />
        </div>
        <p className="hint">
          The short name is what appears under the home-screen icon.
        </p>
      </section>

      <section className="composer">
        <p className="mono">Logo</p>
        <div className="noticeRow">
          {logoV > 0 && (
            <img
              className={plate ? 'logoPlate logoPreview' : 'logoPreview'}
              src={`/branding/logo?v=${logoV}`}
              alt="Current logo"
            />
          )}
          <input
            ref={logoInput}
            type="file"
            accept="image/png,image/jpeg"
            hidden
            onChange={(e) => void pickLogo(e.target.files?.[0])}
          />
          <button onClick={() => logoInput.current?.click()}>
            {logoV > 0 ? 'Replace logo' : 'Upload logo'}
          </button>
          {logoV > 0 && (
            <label className="check">
              <input
                type="checkbox"
                checked={plate}
                onChange={(e) => setPlate(e.target.checked)}
              />
              White backing
            </label>
          )}
        </div>
      </section>


      <section className="composer">
        <p className="mono">Board history</p>
        <div className="noticeRow">
          <input
            type="number"
            min={1}
            max={3650}
            value={windowDays}
            onChange={(e) => setWindowDays(e.target.value)}
            style={{ maxWidth: '7rem' }}
          />
          <span className="hint">days shown on the board</span>
        </div>
        <p className="hint">
          Older notices are not deleted and stay searchable. A screen
          holding a year of history buries this morning's notice.
        </p>
      </section>

      <section className="composer">
        <p className="mono">Counter screens</p>
        <p className="hint">
          Docked pins the app to a screen edge with no minimise or
          close, so it stays visible all day. Each counter PC picks
          this up when it next restarts.
        </p>
        <select value={dock} onChange={(e) => setDock(e.target.value)}>
          <option value="off">Normal window</option>
          <option value="right">Docked to the right</option>
          <option value="left">Docked to the left</option>
        </select>
        {dock !== 'off' && (
          <div className="noticeRow dockWidth">
            <label>
              <span className="mono">Panel width</span>
              <input
                type="range"
                min={320}
                max={640}
                step={20}
                value={dockWidth}
                onChange={(e) => setDockWidth(Number(e.target.value))}
              />
            </label>
            <span className="mono">{dockWidth} px</span>
          </div>
        )}

      </section>

      <section className="composer">
        <p className="mono">Opening hours</p>
        <div className="noticeRow">
          <label className="check">
            Opens
            <input
              type="time"
              value={opens}
              onChange={(e) => setOpens(e.target.value)}
            />
          </label>
          <label className="check">
            Closes
            <input
              type="time"
              value={closes}
              onChange={(e) => setCloses(e.target.value)}
            />
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={quiet}
              onChange={(e) => setQuiet(e.target.checked)}
            />
            Quiet outside these hours
          </label>
        </div>
        <p className="hint">
          Notifications are held outside these hours. Urgent messages
          always go through.
        </p>
      </section>

      <button className="primary" disabled={busy} onClick={save}>
        {busy ? 'Saving...' : 'Save settings'}
      </button>
    </div>
  );
};
